import React, { useState, useRef, useEffect } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const InfoBar = () => {
  const { t, i18n } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef(null);
  const lastTimeRef = useRef(null);
  const rafRef = useRef(null);

  const speed = 60; // pixels per second
  const isRTL = i18n.language === "ur";

  const messages = t("infobar.messages", { returnObjects: true });

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    setOffset(0);
    lastTimeRef.current = null;

    const step = (time) => {
      if (!lastTimeRef.current) lastTimeRef.current = time;
      const delta = (time - lastTimeRef.current) / 1000;
      lastTimeRef.current = time;

      if (!isPaused && containerRef.current) {
        setOffset((prev) => {
          const totalWidth = containerRef.current.scrollWidth / 2;
          const next = isRTL
            ? (prev + speed * delta) % totalWidth // scroll right for Urdu
            : (prev - speed * delta) % totalWidth; // scroll left for English
          return next;
        });
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPaused, isRTL]);

  // Helper: detects if message contains digits or English letters
  const isNumberOrEnglish = (text) => /[A-Za-z0-9+]/.test(text);

  return (
    <div
      className={`fixed top-[64px] left-0 w-full 
                  bg-gradient-to-r from-blue-900 via-blue-700 to-blue-900 
                  text-white py-2 border-b border-blue-500/40 shadow-md 
                  z-40 relative flex items-center overflow-hidden ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Scrolling content */}
      <div
        ref={containerRef}
        className="flex whitespace-nowrap"
        style={{
          transform: `translateX(${isRTL ? -offset : offset}px)`,
          transition: isPaused ? "none" : "transform 0.02s linear",
        }}
      >
        {[0, 1].map((copy) => (
          <div key={copy} className="flex">
            {messages.map((msg, i) => (
              <span
                key={`${copy}-${i}`}
                className={`mx-10 text-sm sm:text-base font-medium tracking-wide ${
                  isNumberOrEnglish(msg) ? "not-rtl" : ""
                }`}
                dir={isNumberOrEnglish(msg) ? "ltr" : isRTL ? "rtl" : "ltr"}
              >
                {msg}
              </span>
            ))}
          </div>
        ))}
      </div>

      {/* Pause / Play Button */}
      <button
        onClick={() => setIsPaused((p) => !p)}
        className={`absolute ${
          isRTL ? "left-4" : "right-4"
        } top-1/2 -translate-y-1/2 
                   bg-blue-800/60 hover:bg-blue-700 text-white 
                   rounded-full p-2 transition-all duration-300 shadow-md`}
        aria-label={isPaused ? "Play scrolling text" : "Pause scrolling text"}
      >
        {isPaused ? <FaPlay size={14} /> : <FaPause size={14} />}
      </button>
    </div>
  );
};

export default InfoBar;
